import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  Datagrid,
  Edit,
  FilterButton,
  FunctionField,
  Labeled,
  List,
  Pagination,
  SaveButton,
  SelectColumnsButton,
  SimpleForm,
  TextField,
  TextInput,
  Toolbar,
  TopToolbar,
  WrapperField,
  useRecordContext,
} from "react-admin";
import { useFormContext } from "react-hook-form";
import { JsonDataView } from "../components/JsonDataView";
import {
  JsonEntryRows,
  removeEntryAtPath,
  renameObjectKeyAtPath,
  tryParseJsonString,
  updateValueAtPath,
} from "../components/JsonEntryRows";
import LocalisedDateField from "../components/localisedDateField";

const listFilters = [<TextInput key="name-filter" label="Name" source="name__icontains" alwaysOn />];
const MyPagination = () => <Pagination rowsPerPageOptions={[10, 25, 50, 100, 500, 1000]} />;

const MyToolbar = (props: any) => {
  return (
    <Toolbar {...props} sx={{ justifyContent: "space-between" }}>
      <SaveButton label="Update" />
    </Toolbar>
  );
};

export const MyActions = () => {
  return (
    <TopToolbar>
      <FilterButton />
      <SelectColumnsButton />
    </TopToolbar>
  );
};

const toSettingsPk = (value: unknown): string => {
  if (typeof value !== "string") {
    return String(value ?? "");
  }

  // IDs can come as Relay-style base64 (e.g. "Settings:123").
  try {
    const decoded = atob(value);
    if (decoded.startsWith("Settings:")) {
      return decoded.replace("Settings:", "");
    }
  } catch (error) {
    // Not base64; return original value.
  }

  return value;
};

const LOCKED_PLATFORM_KEYS = new Set(["IOS", "ANDROID", "WEB"]);

const sortRedirectPayload = (value: any): any => {
  if (Array.isArray(value)) {
    return value.map(sortRedirectPayload);
  }

  if (value && typeof value === "object") {
    const entries = Object.entries(value);
    const sortedKeys = entries
      .map(([key]) => key)
      .sort((a, b) => {
        const aPlatform = LOCKED_PLATFORM_KEYS.has(a.toUpperCase());
        const bPlatform = LOCKED_PLATFORM_KEYS.has(b.toUpperCase());

        if (aPlatform && bPlatform) {
          const platformOrder = ["IOS", "ANDROID", "WEB"];
          return platformOrder.indexOf(a.toUpperCase()) - platformOrder.indexOf(b.toUpperCase());
        }

        if (aPlatform) return 1;
        if (bPlatform) return -1;
        return a.localeCompare(b);
      });

    return sortedKeys.reduce((acc, key) => {
      acc[key] = sortRedirectPayload((value as Record<string, any>)[key]);
      return acc;
    }, {} as Record<string, any>);
  }

  return value;
};

export const SunstarRedirectUrlsList = () => {
  return (
    <List
      resource="settings"
      title="App Deep Links"
      filter={{ name: "sunstar_redirect_url" }}
      filters={listFilters}
      actions={<MyActions />}
      sort={{ field: "createdAt", order: "DESC" }}
      pagination={<MyPagination />}
      exporter={false}
    >
      <Datagrid bulkActionButtons={false} rowClick={false}>
        <FunctionField
          label="Created At"
          render={(record: any) => <LocalisedDateField record={record} field="createdAt" showTime={true} />}
        />
        <TextField source="name" />
        {/* <TextField source="value" /> */}
        <WrapperField label="Actions">
          <FunctionField
            render={(record: any) => (
              <Stack direction="row" spacing={1}>
                <Button
                  component={RouterLink}
                  to={`/sunstar-redirect-urls/${toSettingsPk(record.id)}/show`}
                  size="small"
                  variant="outlined"
                >
                  Show
                </Button>
                <Button
                  component={RouterLink}
                  to={`/sunstar-redirect-urls/${toSettingsPk(record.id)}/edit`}
                  size="small"
                  variant="contained"
                >
                  Edit
                </Button>
              </Stack>
            )}
          />
        </WrapperField>
      </Datagrid>
    </List>
  );
};

const SettingJsonViewer = () => {
  const record = useRecordContext();
  const form = useFormContext<any>();
  const [jsonData, setJsonData] = useState<any>(() => tryParseJsonString((record as any)?.value));
  const [pendingDelete, setPendingDelete] = useState<{
    containerPath: Array<string | number>;
    entryKey: string | number;
    label: string;
  } | null>(null);

  useEffect(() => {
    setJsonData(tryParseJsonString((record as any)?.value));
  }, [record]);

  const isStructuredData = useMemo(
    () => jsonData !== null && typeof jsonData === "object" && !Array.isArray(jsonData),
    [jsonData]
  );

  if (!record) {
    return null;
  }

  const handlePrimitiveChange = (path: Array<string | number>, nextValue: string) => {
    setJsonData((prev: any) => {
      const next = updateValueAtPath(prev, path, nextValue);
      form?.setValue("value", JSON.stringify(next, null, 2), { shouldDirty: true });
      return next;
    });
  };

  const handleKeyRename = (objectPath: Array<string | number>, oldKey: string, nextKeyRaw: string) => {
    if (LOCKED_PLATFORM_KEYS.has(oldKey.toUpperCase())) {
      return;
    }

    const nextKey = nextKeyRaw.trim();
    if (!nextKey || nextKey === oldKey) {
      return;
    }

    setJsonData((prev: any) => {
      const next = renameObjectKeyAtPath(prev, objectPath, oldKey, nextKey);
      form?.setValue("value", JSON.stringify(next, null, 2), { shouldDirty: true });
      return next;
    });
  };

  const handleAddTopLevelField = () => {
    if (!isStructuredData || Array.isArray(jsonData)) {
      return;
    }

    setJsonData((prev: any) => {
      const nextBase = prev && typeof prev === "object" && !Array.isArray(prev) ? prev : {};
      let newKey = "custom_field";
      let suffix = 1;

      while (Object.prototype.hasOwnProperty.call(nextBase, newKey)) {
        suffix += 1;
        newKey = `custom_field_${suffix}`;
      }

      const next = {
        ...nextBase,
        [newKey]: {
          IOS: "",
          ANDROID: "",
          WEB: "",
        },
      };

      form?.setValue("value", JSON.stringify(next, null, 2), { shouldDirty: true });
      return next;
    });
  };

  const handleDeleteField = (containerPath: Array<string | number>, entryKey: string | number) => {
    setJsonData((prev: any) => {
      const next = removeEntryAtPath(prev, containerPath, entryKey);
      form?.setValue("value", JSON.stringify(next, null, 2), { shouldDirty: true });
      return next;
    });
  };

  const requestDeleteField = (containerPath: Array<string | number>, entryKey: string | number, label: string) => {
    setPendingDelete({ containerPath: [...containerPath], entryKey, label });
  };

  const confirmDeleteField = () => {
    if (!pendingDelete) {
      return;
    }

    handleDeleteField(pendingDelete.containerPath, pendingDelete.entryKey);
    setPendingDelete(null);
  };

  return (
    <Stack spacing={2} sx={{ mt: 2 }}>
      <Typography variant="subtitle1">JSON Viewer</Typography>
      {form ? (
        isStructuredData ? (
          <>
            <JsonEntryRows
              value={jsonData}
              lockedKeys={LOCKED_PLATFORM_KEYS}
              onPrimitiveChange={handlePrimitiveChange}
              onKeyRename={handleKeyRename}
              onDeleteRequest={requestDeleteField}
            />
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddTopLevelField}
              sx={{ alignSelf: "flex-start", mt: 1 }}
            >
              Add Field
            </Button>
          </>
        ) : (
          <JsonDataView data={jsonData} emptyText="No redirect data" />
        )
      ) : (
        <JsonDataView data={jsonData} emptyText="No redirect data" />
      )}

      <Dialog open={Boolean(pendingDelete)} onClose={() => setPendingDelete(null)}>
        <DialogTitle>Delete field?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <b>{pendingDelete?.label || "this field"}</b>? This change will be saved
            when you click Update.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDeleteField}>
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

const SettingJsonViewerReadOnly = () => {
  const record = useRecordContext();

  if (!record) {
    return null;
  }

  const jsonData = tryParseJsonString((record as any).value);
  const isStructuredData = jsonData !== null && typeof jsonData === "object";

  return (
    <Stack spacing={2} sx={{ mt: 2 }}>
      <Typography variant="subtitle1">JSON Viewer</Typography>
      {isStructuredData ? (
        <JsonEntryRows value={jsonData} lockedKeys={LOCKED_PLATFORM_KEYS} readOnly />
      ) : (
        <JsonDataView data={jsonData} emptyText="No redirect data" />
      )}
    </Stack>
  );
};

const SunstarRedirectUrlsCreateEditForm: React.FC<{ readOnly?: boolean }> = ({ readOnly = false }) => {
  return (
    <Grid container columnSpacing={2}>
      <Grid item xs={12} sm={11}>
        <Labeled label="Name" fullWidth={true}>
          <TextInput source="name" fullWidth={true} disabled />
        </Labeled>
      </Grid>

      <Grid item xs={12} sm={11}>
        <Labeled label="Created At" fullWidth={true}>
          <FunctionField render={(record: any) => <LocalisedDateField record={record} field="createdAt" />} />
        </Labeled>
        {readOnly ? <SettingJsonViewerReadOnly /> : <SettingJsonViewer />}
      </Grid>
    </Grid>
  );
};

export const SunstarRedirectUrlsEdit = () => {
  const { id } = useParams();
  const settingsId = toSettingsPk(id);
  const sanitizePayload = (data: any) => {
    const { createdAt, created_at, updatedAt, updated_at, ...rest } = data || {};

    if (typeof rest.value === "string") {
      const parsed = tryParseJsonString(rest.value);
      if (parsed && typeof parsed === "object") {
        rest.value = JSON.stringify(sortRedirectPayload(parsed), null, 2);
      }
    }

    return rest;
  };

  return (
    <Edit
      resource="settings"
      id={settingsId}
      title="Edit Sunstar Redirect Url"
      mutationMode="pessimistic"
      redirect={false}
      transform={sanitizePayload}
    >
      <SimpleForm toolbar={<MyToolbar />}>
        <TextInput source="value" sx={{ display: "none" }} />
        <SunstarRedirectUrlsCreateEditForm />
      </SimpleForm>
    </Edit>
  );
};

export const SunstarRedirectUrlsShow = () => {
  const { id } = useParams();
  const settingsId = toSettingsPk(id);

  return (
    <Edit resource="settings" id={settingsId} title="Sunstar Redirect Url" mutationMode="pessimistic" redirect={false}>
      <SimpleForm toolbar={false}>
        <SunstarRedirectUrlsCreateEditForm readOnly />
      </SimpleForm>
    </Edit>
  );
};

const SunstarRedirectUrls = () => <SunstarRedirectUrlsList />;

export default SunstarRedirectUrls;
